import { useState } from "react";
import api, { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Download, Upload, FileSpreadsheet } from "lucide-react";

export default function ImportExport() {
  const [file, setFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);

  const handleExport = async () => {
    try {
      const res = await fetch(`${API}/youth-export/csv`, {
        credentials: "include",
        headers: localStorage.getItem("psi_token")
          ? { Authorization: `Bearer ${localStorage.getItem("psi_token")}` }
          : {},
      });
      if (!res.ok) throw new Error("Export failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `padaskillinfo_youth_${new Date().toISOString().split("T")[0]}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("CSV downloaded");
    } catch (e) {
      toast.error("Export failed");
    }
  };

  const handleImport = async () => {
    if (!file) return toast.error("Select a CSV file");
    setImporting(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/youth-import/csv", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      toast.success(`Imported ${data.created} records`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed");
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="p-6 lg:p-8" data-testid="import-export-page">
      <div className="mb-6">
        <h1 className="font-heading font-bold text-3xl">Import / Export</h1>
        <p className="text-gray-600 text-sm">Bulk operations for the census database</p>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Export */}
        <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-[0_2px_10px_rgba(0,0,0,0.04)]">
          <div className="w-12 h-12 rounded-xl bg-[#1E3A8A] flex items-center justify-center mb-4">
            <Download className="w-6 h-6 text-white" />
          </div>
          <h3 className="font-heading font-bold text-xl mb-2">Export CSV</h3>
          <p className="text-sm text-gray-600 mb-5">
            Download the complete youth registration database as a CSV file.
            Includes all fields: personal, education, domains, employment, bank linkage.
          </p>
          <Button onClick={handleExport} className="bg-[#1E3A8A] hover:bg-[#1E3A8A]/90 w-full" data-testid="export-csv-btn">
            <Download className="w-4 h-4 mr-2" /> Download CSV
          </Button>
        </div>

        {/* Import */}
        <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-[0_2px_10px_rgba(0,0,0,0.04)]">
          <div className="w-12 h-12 rounded-xl bg-[#F59E0B] flex items-center justify-center mb-4">
            <Upload className="w-6 h-6 text-white" />
          </div>
          <h3 className="font-heading font-bold text-xl mb-2">Bulk Import</h3>
          <p className="text-sm text-gray-600 mb-4">
            Upload a CSV with youth records. Required columns: full_name, aadhaar_number, mobile, village.
            Comma-separate domains/skills inside cells.
          </p>
          <div className="space-y-3">
            <Input type="file" accept=".csv" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="csv-file-input" />
            <Button onClick={handleImport} disabled={importing || !file} className="bg-[#F59E0B] hover:bg-[#F59E0B]/90 w-full" data-testid="import-csv-btn">
              <Upload className="w-4 h-4 mr-2" /> {importing ? "Importing…" : "Import CSV"}
            </Button>
          </div>

          {result && (
            <div className="mt-4 p-3 bg-gray-50 rounded-md text-sm">
              <div className="flex items-center gap-2 font-heading font-semibold text-[#10B981]">
                <FileSpreadsheet className="w-4 h-4" /> {result.created} record(s) imported
              </div>
              {result.errors?.length > 0 && (
                <details className="mt-2">
                  <summary className="cursor-pointer text-[#E11D48] text-xs">View {result.errors.length} error(s)</summary>
                  <ul className="text-xs text-gray-600 mt-1 list-disc pl-5">
                    {result.errors.map((e, i) => <li key={`err-${i}`}>{e}</li>)}
                  </ul>
                </details>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Sample format */}
      <div className="bg-white rounded-2xl border border-gray-100 p-6 mt-6">
        <h3 className="font-heading font-bold text-lg mb-3">Sample CSV format</h3>
        <pre className="bg-gray-50 p-4 rounded-md text-xs overflow-x-auto">
{`full_name,aadhaar_number,mobile,gender,date_of_birth,village,education_level,education_status,employment_status,placement_preference,interested_domains,ai_upskill_interest,bank_linkage_required
Rama Krishna,123456789012,9876543210,Male,2000-05-12,Pithapuram,Graduate,Completed,Unemployed,Both,"Artificial Intelligence, Web Development",true,false`}
        </pre>
      </div>
    </div>
  );
}
